import { knowledgeBaseArticlesPath, knowledgeBaseCollectionsPath, knowledgeBaseNewArticlePath, settingsSectionPaths, viewPaths } from "./data";
import type {
  Customer,
  CustomerActionHistoryEntry,
  CustomerContact,
  CustomerProduct,
  RolePreset,
  SettingsSection,
  TicketCategory,
  TicketCategoryCustomField,
  TicketCategoryCustomFieldType,
  TicketStatus,
  UserTeam,
  View,
  WorkspaceMember,
} from "./types";
import { getActiveAppLanguage, getActiveIntlLocale } from "./language";
import { canEditTickets, isAgentRole } from "../roles";

export type DateTimeDisplayMode = "relative" | "absolute";

const timezoneLabelToIanaMap: Record<string, string> = {
  "UTC-08 · San Francisco": "America/Los_Angeles",
  "UTC-05 · New York": "America/New_York",
  "UTC+00 · London": "Europe/London",
  "UTC+01 · Berlin": "Europe/Berlin",
  "UTC+08 · Singapore": "Asia/Singapore",
  "UTC+09 · Tokyo": "Asia/Tokyo",
  "UTC+10 · Sydney": "Australia/Sydney",
};

function normalizePathname(pathname: string) {
  const normalizedPathname = pathname.trim();

  if (!normalizedPathname || normalizedPathname === "/") {
    return "/";
  }

  return normalizedPathname.replace(/\/+$/, "") || "/";
}

export function getViewFromPath(pathname: string): View {
  const normalizedPathname = normalizePathname(pathname);

  if (normalizedPathname.startsWith(`${viewPaths.Customers}/`)) {
    return "Customers";
  }

  if (normalizedPathname.startsWith(`${viewPaths.Tickets}/`)) {
    return "Tickets";
  }

  if (normalizedPathname === viewPaths["Knowledge Base"] || normalizedPathname.startsWith(`${viewPaths["Knowledge Base"]}/`)) {
    return "Knowledge Base";
  }

  if (normalizedPathname === viewPaths.Notifications || normalizedPathname.startsWith(`${viewPaths.Notifications}/`)) {
    return "Notifications";
  }

  if (normalizedPathname === "/analytics" || normalizedPathname.startsWith("/analytics/")) {
    return "Reporting";
  }

  if (normalizedPathname === viewPaths.Logs || normalizedPathname.startsWith(`${viewPaths.Logs}/`)) {
    return "Logs";
  }

  if (normalizedPathname === viewPaths.Settings || normalizedPathname.startsWith(`${viewPaths.Settings}/`)) {
    return "Settings";
  }

  const entry = Object.entries(viewPaths).find(([, path]) => path === normalizedPathname);
  return (entry?.[0] as View | undefined) ?? "Dashboard";
}

export function getSettingsSectionFromPath(pathname: string): SettingsSection | null {
  const normalizedPathname = normalizePathname(pathname);

  const entry = Object.entries(settingsSectionPaths).find(([, path]) => normalizedPathname === path);
  return (entry?.[0] as SettingsSection | undefined) ?? null;
}

export function getCustomerIdFromPath(pathname: string) {
  const normalizedPathname = normalizePathname(pathname);

  if (!normalizedPathname.startsWith(`${viewPaths.Customers}/`)) {
    return null;
  }

  const customerId = normalizedPathname.slice(`${viewPaths.Customers}/`.length).trim();
  return customerId.length > 0 ? decodeURIComponent(customerId) : null;
}

export function getTicketIdFromPath(pathname: string) {
  const normalizedPathname = normalizePathname(pathname);

  if (!normalizedPathname.startsWith(`${viewPaths.Tickets}/`)) {
    return null;
  }

  const ticketId = normalizedPathname.slice(`${viewPaths.Tickets}/`.length).trim();
  return ticketId.length > 0 ? decodeURIComponent(ticketId) : null;
}

export function isKnowledgeBaseNewArticlePath(pathname: string) {
  return normalizePathname(pathname) === knowledgeBaseNewArticlePath;
}

export function getKnowledgeCollectionIdFromPath(pathname: string) {
  const normalizedPathname = normalizePathname(pathname);

  if (!normalizedPathname.startsWith(`${knowledgeBaseCollectionsPath}/`)) {
    return null;
  }

  const collectionId = normalizedPathname.slice(`${knowledgeBaseCollectionsPath}/`.length).trim();
  return collectionId.length > 0 ? decodeURIComponent(collectionId) : null;
}

export function getKnowledgeArticleIdFromPath(pathname: string) {
  const normalizedPathname = normalizePathname(pathname);

  if (!normalizedPathname.startsWith(`${knowledgeBaseArticlesPath}/`)) {
    return null;
  }

  const articlePath = normalizedPathname.slice(`${knowledgeBaseArticlesPath}/`.length).trim();

  if (!articlePath) {
    return null;
  }

  const [articleId] = articlePath.split("/");
  return articleId ? decodeURIComponent(articleId) : null;
}

export function isKnowledgeArticleEditPath(pathname: string) {
  return /^\/knowledge-base\/articles\/[^/]+\/edit$/.test(pathname);
}

export function getStatusIcon(status: TicketStatus) {
  switch (status) {
    case "In Progress":
      return "●";
    case "Waiting customer":
      return "◔";
    case "Resolved":
      return "✓";
    case "Pending":
      return "◌";
    case "Open":
      return "!";
  }
}

export function formatCount(value: number) {
  return value.toString().padStart(2, "0");
}

export function createRecordId(prefix: string) {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return `${prefix}-${crypto.randomUUID()}`;
  }

  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

export function normalizeTicketCategoryCustomFieldType(value: unknown): TicketCategoryCustomFieldType {
  switch (value) {
    case "textarea":
    case "select":
    case "date":
      return value;
    default:
      return "text";
  }
}

function normalizeCustomFieldOptions(value: unknown) {
  if (!Array.isArray(value)) {
    return [] as string[];
  }

  return [...new Set(value
    .map(option => (typeof option === "string" ? option.trim() : ""))
    .filter(Boolean))];
}

export function normalizeTicketCategoryCustomFields(value: unknown): TicketCategoryCustomField[] {
  if (!Array.isArray(value)) {
    return [];
  }

  const seenIds = new Set<string>();

  return value.flatMap((field, index) => {
    if (!field || typeof field !== "object") {
      return [];
    }

    const record = field as Partial<TicketCategoryCustomField>;
    const label = typeof record.label === "string" ? record.label.trim() : "";

    if (!label) {
      return [];
    }

    const rawId = typeof record.id === "string" ? record.id.trim() : "";
    const fallbackId = `ticket-field-${index + 1}`;
    let id = rawId || fallbackId;

    if (seenIds.has(id)) {
      id = `${id}-${index + 1}`;
    }

    seenIds.add(id);

    const type = normalizeTicketCategoryCustomFieldType(record.type);

    return [{
      id,
      label,
      type,
      required: Boolean(record.required),
      helpText: typeof record.helpText === "string" ? record.helpText.trim() : "",
      options: type === "select" ? normalizeCustomFieldOptions(record.options) : [],
    }];
  });
}

export function normalizeTicketCustomFieldValues(value: unknown): Record<string, string> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  return Object.entries(value).reduce<Record<string, string>>((result, [fieldId, fieldValue]) => {
    const normalizedFieldId = fieldId.trim();

    if (!normalizedFieldId) {
      return result;
    }

    result[normalizedFieldId] = typeof fieldValue === "string"
      ? fieldValue.trim()
      : fieldValue === null || fieldValue === undefined
        ? ""
        : String(fieldValue).trim();

    return result;
  }, {});
}

export function syncTicketCustomFieldValues(
  customFields: TicketCategoryCustomField[],
  values: Record<string, string> | null | undefined,
): Record<string, string> {
  const normalizedValues = normalizeTicketCustomFieldValues(values);

  return customFields.reduce<Record<string, string>>((result, field) => {
    const nextValue = normalizedValues[field.id] ?? "";

    result[field.id] = field.type === "select" && nextValue && !field.options.includes(nextValue)
      ? ""
      : nextValue;

    return result;
  }, {});
}

export function createCustomerActionHistoryEntry(
  entry: Omit<CustomerActionHistoryEntry, "id" | "createdAt"> & { createdAt?: string },
): CustomerActionHistoryEntry {
  return {
    id: createRecordId("customer-history"),
    createdAt: entry.createdAt ?? new Date().toISOString(),
    entity: entry.entity,
    action: entry.action,
    detail: entry.detail,
    actor: entry.actor,
  };
}

export function appendCustomerActionHistory(
  customer: Customer,
  entry: Omit<CustomerActionHistoryEntry, "id" | "createdAt"> & { createdAt?: string },
): Customer {
  return {
    ...customer,
    actionHistory: [createCustomerActionHistoryEntry(entry), ...(customer.actionHistory ?? [])],
  };
}

export function getInitials(value: string) {
  const cleaned = value.trim();

  if (cleaned.length === 0) {
    return "--";
  }

  const initials = cleaned
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase() ?? "")
    .join("");

  return initials || cleaned.slice(0, 2).toUpperCase();
}

function parseDateValue(value: string) {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year = 0, month = 1, day = 1] = value.split("-").map(Number);
    return new Date(year, month - 1, day, 12);
  }

  return new Date(value);
}

function parseDateTimeValue(value: string) {
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) {
    const [datePart = "", timePart = ""] = value.split("T");
    const [year = 0, month = 1, day = 1] = datePart.split("-").map(Number);
    const [hour = 0, minute = 0] = timePart.split(":").map(Number);
    return new Date(year, month - 1, day, hour, minute);
  }

  const utcLikeDateTimeMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2})(\.\d{1,3})?)?$/);

  if (utcLikeDateTimeMatch) {
    const [, yearText, monthText, dayText, hourText, minuteText, secondText = "0", millisecondText = ""] = utcLikeDateTimeMatch;
    const milliseconds = millisecondText ? Number(millisecondText.slice(1).padEnd(3, "0")) : 0;

    return new Date(
      Date.UTC(
        Number(yearText),
        Number(monthText) - 1,
        Number(dayText),
        Number(hourText),
        Number(minuteText),
        Number(secondText),
        milliseconds,
      ),
    );
  }

  return parseDateValue(value);
}

export function parseTimestampValue(value?: string | null, referenceNow = Date.now()) {
  const normalizedValue = value?.trim();

  if (!normalizedValue) {
    return Number.NaN;
  }

  const parsedDate = parseDateTimeValue(normalizedValue);

  if (!Number.isNaN(parsedDate.getTime())) {
    return parsedDate.getTime();
  }

  const loweredValue = normalizedValue.toLowerCase();

  if (loweredValue === "just now") {
    return referenceNow;
  }

  const relativeMatch = loweredValue.match(/^(\d+)\s+(sec|secs|second|seconds|min|mins|minute|minutes|hour|hours|day|days|week|weeks|month|months|year|years)\s+ago$/);

  if (!relativeMatch) {
    return Number.NaN;
  }

  const [, amountText = "0", unit = ""] = relativeMatch;
  const amount = Number(amountText);

  if (!Number.isFinite(amount)) {
    return Number.NaN;
  }

  const unitDuration = unit.startsWith("sec")
    ? 1_000
    : unit.startsWith("min")
      ? 60_000
      : unit.startsWith("hour")
        ? 3_600_000
        : unit.startsWith("day")
          ? 86_400_000
          : unit.startsWith("week")
            ? 7 * 86_400_000
            : unit.startsWith("month")
              ? 30 * 86_400_000
              : 365 * 86_400_000;

  return referenceNow - amount * unitDuration;
}

export function resolveTimeZone(timezone?: string | null) {
  const normalizedTimezone = timezone?.trim();

  if (!normalizedTimezone) {
    return null;
  }

  const candidateTimezone = timezoneLabelToIanaMap[normalizedTimezone] ?? normalizedTimezone;

  try {
    new Intl.DateTimeFormat(getActiveIntlLocale(), { timeZone: candidateTimezone }).format(new Date());
    return candidateTimezone;
  } catch {
    return null;
  }
}

export function getDateTimePartsInTimeZone(date: Date, timezone?: string | null) {
  const resolvedTimeZone = resolveTimeZone(timezone);

  if (!resolvedTimeZone) {
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
      hour: date.getHours(),
      minute: date.getMinutes(),
      second: date.getSeconds(),
    };
  }

  const formatter = new Intl.DateTimeFormat(getActiveIntlLocale(), {
    timeZone: resolvedTimeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    hourCycle: "h23",
  });
  const parts = formatter.formatToParts(date);
  const getNumberPart = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find(part => part.type === type)?.value ?? "0");

  return {
    year: getNumberPart("year"),
    month: getNumberPart("month"),
    day: getNumberPart("day"),
    hour: getNumberPart("hour"),
    minute: getNumberPart("minute"),
    second: getNumberPart("second"),
  };
}

function getTimeZoneOffsetMinutes(date: Date, timezone: string) {
  const parts = getDateTimePartsInTimeZone(date, timezone);
  const utcTimestamp = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
  return (utcTimestamp - date.getTime()) / 60_000;
}

function parseDateTimeInputParts(value: string) {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/);

  if (!match) {
    return null;
  }

  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4]),
    minute: Number(match[5]),
    second: Number(match[6] ?? "0"),
  };
}

export function formatDateForInput(value?: string) {
  if (!value) {
    return "";
  }

  const parsed = parseDateValue(value);

  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

export function formatDateLabel(value: string) {
  const parsed = parseDateValue(value);

  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(getActiveIntlLocale(), {
    month: "short",
    day: "2-digit",
    year: "numeric",
  }).format(parsed);
}

export function normalizeDateTimeForStorage(value?: string | null, timezone?: string | null) {
  const normalizedValue = value?.trim() ?? "";

  if (!normalizedValue) {
    return null;
  }

  const inputParts = parseDateTimeInputParts(normalizedValue);
  const resolvedTimeZone = resolveTimeZone(timezone);

  if (inputParts && resolvedTimeZone) {
    const utcGuess = Date.UTC(inputParts.year, inputParts.month - 1, inputParts.day, inputParts.hour, inputParts.minute, inputParts.second);
    let offsetMinutes = getTimeZoneOffsetMinutes(new Date(utcGuess), resolvedTimeZone);
    let adjustedDate = new Date(utcGuess - offsetMinutes * 60_000);
    const refinedOffsetMinutes = getTimeZoneOffsetMinutes(adjustedDate, resolvedTimeZone);

    if (refinedOffsetMinutes !== offsetMinutes) {
      adjustedDate = new Date(utcGuess - refinedOffsetMinutes * 60_000);
    }

    return Number.isNaN(adjustedDate.getTime()) ? null : adjustedDate.toISOString();
  }

  const parsed = parseDateTimeValue(normalizedValue);

  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed.toISOString();
}

export function formatDateTimeForInput(value?: string | null, timezone?: string | null, includeSeconds = false) {
  if (!value) {
    return "";
  }

  const parsed = parseDateTimeValue(value);

  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  const parts = getDateTimePartsInTimeZone(parsed, timezone);
  const year = parts.year;
  const month = String(parts.month).padStart(2, "0");
  const day = String(parts.day).padStart(2, "0");
  const hours = String(parts.hour).padStart(2, "0");
  const minutes = String(parts.minute).padStart(2, "0");
  const seconds = String(parts.second).padStart(2, "0");

  return includeSeconds
    ? `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`
    : `${year}-${month}-${day}T${hours}:${minutes}`;
}

export function formatDateTimeLabel(value?: string | null, fallback = "Not scheduled", timezone?: string | null) {
  if (!value) {
    return fallback;
  }

  const parsed = parseDateTimeValue(value);

  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  const resolvedTimeZone = resolveTimeZone(timezone);

  return new Intl.DateTimeFormat(getActiveIntlLocale(), {
    ...(resolvedTimeZone ? { timeZone: resolvedTimeZone } : {}),
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(parsed);
}

export function formatScheduleRangeLabel(
  startValue?: string | null,
  endValue?: string | null,
  fallback = "Not scheduled",
  timezone?: string | null,
) {
  if (startValue && endValue) {
    return `${formatDateTimeLabel(startValue, fallback, timezone)} → ${formatDateTimeLabel(endValue, fallback, timezone)}`;
  }

  return formatDateTimeLabel(endValue ?? startValue, fallback, timezone);
}

export function getSearchRelevance(query: string, fields: Array<string | null | undefined>) {
  const normalizedFields = fields
    .map(field => field?.trim().toLowerCase() ?? "")
    .filter(Boolean);

  if (normalizedFields.length === 0) {
    return 0;
  }

  const terms = query.split(/\s+/).filter(Boolean);

  if (terms.length === 0) {
    return 0;
  }

  let score = 0;

  for (const term of terms) {
    let matched = false;

    for (const field of normalizedFields) {
      if (field === term) {
        score += 120;
        matched = true;
        break;
      }

      if (field.startsWith(term)) {
        score += 80;
        matched = true;
        break;
      }

      if (field.includes(` ${term}`)) {
        score += 60;
        matched = true;
        break;
      }

      if (field.includes(term)) {
        score += 40;
        matched = true;
        break;
      }
    }

    if (!matched) {
      return 0;
    }
  }

  if (normalizedFields.some(field => field === query)) {
    score += 160;
  } else if (normalizedFields.some(field => field.startsWith(query))) {
    score += 80;
  } else if (normalizedFields.some(field => field.includes(query))) {
    score += 30;
  }

  return score;
}

export function formatPreciseDateTimeLabel(value?: string | null, fallback = "Unknown", timezone?: string | null) {
  if (!value) {
    return fallback;
  }

  const parsed = parseDateTimeValue(value);

  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  const resolvedTimeZone = resolveTimeZone(timezone);

  return new Intl.DateTimeFormat(getActiveIntlLocale(), {
    ...(resolvedTimeZone ? { timeZone: resolvedTimeZone } : {}),
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(parsed);
}

export function formatRelativeDateTimeLabel(value?: string | null, fallback = "Unknown", now = Date.now()) {
  if (!value) {
    return fallback;
  }

  const timestamp = parseTimestampValue(value, now);

  if (Number.isNaN(timestamp)) {
    return value;
  }

  const difference = timestamp - now;
  const absoluteDifference = Math.abs(difference);
  const language = getActiveAppLanguage();

  if (absoluteDifference < 45_000) {
    return language === "ja" ? "たった今" : "Just now";
  }

  const formatter = new Intl.RelativeTimeFormat(language, { numeric: "auto" });

  if (absoluteDifference < 3_600_000) {
    return formatter.format(Math.round(difference / 60_000), "minute");
  }

  if (absoluteDifference < 86_400_000) {
    return formatter.format(Math.round(difference / 3_600_000), "hour");
  }

  if (absoluteDifference < 2_592_000_000) {
    return formatter.format(Math.round(difference / 86_400_000), "day");
  }

  if (absoluteDifference < 31_536_000_000) {
    return formatter.format(Math.round(difference / 2_592_000_000), "month");
  }

  return formatter.format(Math.round(difference / 31_536_000_000), "year");
}

export function formatDurationSinceLabel(value?: string | null, fallback = "Unknown", now = Date.now()) {
  if (!value) {
    return fallback;
  }

  const timestamp = parseTimestampValue(value, now);

  if (Number.isNaN(timestamp)) {
    return value;
  }

  const elapsed = Math.max(0, now - timestamp);
  const language = getActiveAppLanguage();

  if (elapsed < 60_000) {
    return language === "ja" ? "1分未満" : "< 1 min";
  }

  if (elapsed < 3_600_000) {
    const minutes = Math.floor(elapsed / 60_000);
    return language === "ja" ? `${minutes}分` : `${minutes} min${minutes === 1 ? "" : "s"}`;
  }

  if (elapsed < 86_400_000) {
    const hours = Math.floor(elapsed / 3_600_000);
    return language === "ja" ? `${hours}時間` : `${hours} hr${hours === 1 ? "" : "s"}`;
  }

  if (elapsed < 2_592_000_000) {
    const days = Math.floor(elapsed / 86_400_000);
    return language === "ja" ? `${days}日` : `${days} day${days === 1 ? "" : "s"}`;
  }

  if (elapsed < 31_536_000_000) {
    const months = Math.floor(elapsed / 2_592_000_000);
    return language === "ja" ? `${months}か月` : `${months} mo${months === 1 ? "" : "s"}`;
  }

  const years = Math.floor(elapsed / 31_536_000_000);
  return language === "ja" ? `${years}年` : `${years} yr${years === 1 ? "" : "s"}`;
}

export function formatTicketSlaLabel(value: string | null | undefined) {
  const normalizedValue = value?.trim() ?? "";

  if (!normalizedValue) {
    return "No SLA";
  }

  if (/(hour|hours|hr|hrs|minute|minutes|min|mins)/i.test(normalizedValue)) {
    return normalizedValue
      .replace(/\bhrs?\b/i, match => match.toLowerCase().startsWith("hr") ? "hours" : match)
      .replace(/\bmins?\b/i, match => match.toLowerCase().startsWith("min") ? "minutes" : match);
  }

  return `${normalizedValue} hours`;
}

export function parseTicketSlaDurationMs(value: string | null | undefined) {
  const normalizedValue = value?.trim() ?? "";

  if (!normalizedValue) {
    return null;
  }

  const matchedValue = normalizedValue.match(/(\d+(?:\.\d+)?)\s*(hour|hours|hr|hrs|minute|minutes|min|mins)?/i);

  if (!matchedValue) {
    return null;
  }

  const amount = Number(matchedValue[1] ?? "");
  const unitLabel = (matchedValue[2] ?? "hours").toLowerCase();

  if (!Number.isFinite(amount) || amount <= 0) {
    return null;
  }

  const unitDurationMs = unitLabel.startsWith("min") ? 60_000 : 3_600_000;
  return Math.round(amount * unitDurationMs);
}

function normalizePausedDurationMs(value: number | null | undefined) {
  const normalizedValue = Number(value ?? 0);

  if (!Number.isFinite(normalizedValue) || normalizedValue < 0) {
    return 0;
  }

  return Math.round(normalizedValue);
}

export function getTicketSlaDeadlineTimestamp(
  createdAt: string | null | undefined,
  sla: string | null | undefined,
  now = Date.now(),
  slaPausedAt?: string | null,
  slaPausedTotalMs?: number | null,
) {
  const createdAtTimestamp = parseTimestampValue(createdAt, now);
  const slaDurationMs = parseTicketSlaDurationMs(sla);

  if (Number.isNaN(createdAtTimestamp) || slaDurationMs === null) {
    return null;
  }

  const pausedStartedAtTimestamp = slaPausedAt ? parseTimestampValue(slaPausedAt, now) : Number.NaN;
  const totalPausedMs = normalizePausedDurationMs(slaPausedTotalMs);
  const currentPausedMs = Number.isNaN(pausedStartedAtTimestamp) ? 0 : Math.max(0, now - pausedStartedAtTimestamp);

  return createdAtTimestamp + slaDurationMs + totalPausedMs + currentPausedMs;
}

function formatSignedClockMinutes(totalMinutes: number) {
  const absoluteMinutes = Math.abs(totalMinutes);
  const hours = Math.floor(absoluteMinutes / 60);
  const minutes = absoluteMinutes % 60;
  const prefix = totalMinutes < 0 ? "-" : "";

  return `${prefix}${hours}:${String(minutes).padStart(2, "0")}`;
}

export function getTicketSlaCountdownInfo(
  createdAt: string | null | undefined,
  sla: string | null | undefined,
  now = Date.now(),
  slaPausedAt?: string | null,
  slaPausedTotalMs?: number | null,
) {
  const deadlineTimestamp = getTicketSlaDeadlineTimestamp(createdAt, sla, now, slaPausedAt, slaPausedTotalMs);

  if (deadlineTimestamp === null) {
    return null;
  }

  const remainingMs = deadlineTimestamp - now;
  const roundedMinutes = Math.abs(remainingMs) < 60_000
    ? 0
    : remainingMs > 0
      ? Math.ceil(remainingMs / 60_000)
      : -Math.floor(Math.abs(remainingMs) / 60_000);

  return {
    deadlineTimestamp,
    remainingMs,
    isOverdue: remainingMs < 0,
    isPaused: Boolean(slaPausedAt),
    displayLabel: formatSignedClockMinutes(roundedMinutes),
  };
}

export function formatTicketLastUpdateLabel(
  value: string | null | undefined,
  timezone?: string | null,
  mode: DateTimeDisplayMode = "relative",
  fallback = "Unknown",
  now = Date.now(),
) {
  return mode === "absolute"
    ? formatPreciseDateTimeLabel(value, fallback, timezone)
    : formatRelativeDateTimeLabel(value, fallback, now);
}

export function getWorkspaceMemberEffectiveCategoryIds(
  member: Pick<WorkspaceMember, "role" | "allowedCategoryIds" | "teamIds">,
  teams: UserTeam[],
) {
  if (!isAgentRole(member.role) || member.allowedCategoryIds === null) {
    return null;
  }

  const teamById = new Map(teams.map(team => [team.id, team]));
  const effectiveCategoryIds = new Set(member.allowedCategoryIds ?? []);

  for (const teamId of member.teamIds ?? []) {
    const team = teamById.get(teamId);

    if (!team) {
      continue;
    }

    for (const categoryId of team.categoryIds) {
      effectiveCategoryIds.add(categoryId);
    }
  }

  return [...effectiveCategoryIds];
}

export function getAssignableAssigneeNames(members: WorkspaceMember[], ticketCategories: TicketCategory[], categoryName?: string | null, rolePresets?: RolePreset[]) {
  const normalizedCategoryName = categoryName?.trim().toLowerCase() ?? "";
  const categoryId = ticketCategories.find(category => category.name.trim().toLowerCase() === normalizedCategoryName)?.id ?? null;

  return [...new Set(
    members
      .filter(member => member.status === "Active")
      .filter(member => member.canEditTickets ?? canEditTickets(member.role, rolePresets))
      .filter(member => !isAgentRole(member.role) || (member.effectiveCategoryIds ?? member.allowedCategoryIds) === null || categoryId === null || (member.effectiveCategoryIds ?? member.allowedCategoryIds ?? []).includes(categoryId))
      .map(member => member.name.trim())
      .filter(Boolean),
  )].sort((left, right) => left.localeCompare(right, undefined, { sensitivity: "base" }));
}

export function resolveWorkspaceMemberByEmail(members: WorkspaceMember[], email: string | null | undefined) {
  const normalizedEmail = email?.trim().toLowerCase() ?? "";

  if (!normalizedEmail) {
    return null;
  }

  return members.find(member => member.email.trim().toLowerCase() === normalizedEmail) ?? null;
}

export function resolveWorkspaceMember(members: WorkspaceMember[], identity: string | null | undefined) {
  const normalizedIdentity = identity?.trim().toLowerCase() ?? "";

  if (!normalizedIdentity) {
    return null;
  }

  return members.find(member => (
    member.name.trim().toLowerCase() === normalizedIdentity
    || member.email.trim().toLowerCase() === normalizedIdentity
  )) ?? null;
}

function normalizeContacts(customer: Customer) {
  if (customer.contacts.length > 0) {
    return customer.contacts.map(contact => ({
      ...contact,
      id: contact.id || createRecordId(`${customer.id}-contact`),
    }));
  }

  const derivedContacts: CustomerContact[] = [];

  if (customer.name || customer.email || customer.phone) {
    derivedContacts.push({
      id: createRecordId(`${customer.id}-contact`),
      role: "Primary",
      name: customer.name,
      email: customer.email,
      phone: customer.phone,
    });
  }

  if (customer.secondaryContact.name || customer.secondaryContact.email) {
    derivedContacts.push({
      id: createRecordId(`${customer.id}-contact`),
      role: "Secondary",
      name: customer.secondaryContact.name,
      email: customer.secondaryContact.email,
    });
  }

  return derivedContacts;
}

function normalizeProducts(customer: Customer) {
  return customer.products.map((product, index) => ({
    ...product,
    id: product.id || `${customer.id}-product-${index + 1}`,
    productPlanId: product.productPlanId || undefined,
    seats: Math.max(0, product.seats ?? 0),
  })) satisfies CustomerProduct[];
}

export function getCustomerSeatCount(customer: Customer) {
  return customer.products.reduce((total, product) => total + product.seats, 0);
}

export function syncCustomerFields(customer: Customer): Customer {
  const contacts = normalizeContacts(customer);
  const primaryContact = contacts.find(contact => contact.role === "Primary") ?? contacts[0];
  const secondaryContact = contacts.find(contact => contact.role === "Secondary") ?? contacts.find(contact => contact.id !== primaryContact?.id);
  const fallbackIdentity = primaryContact?.name || customer.company;
  const accountStatus = customer.health === "Disabled" ? "Disabled" : "Active";

  return {
    ...customer,
    initials: getInitials(fallbackIdentity),
    name: primaryContact?.name ?? "",
    email: primaryContact?.email ?? "",
    phone: primaryContact?.phone ?? "",
    accountStatus,
    contacts,
    products: normalizeProducts(customer),
    actionHistory: customer.actionHistory ?? [],
    secondaryContact: {
      name: secondaryContact?.name ?? "",
      email: secondaryContact?.email ?? "",
    },
  };
}
